import { AIRPORT_DATABASE } from './airportDatabase';

const ARROW_PATTERN = /([A-Za-z0-9]{3})\s*(?:→|->|—|-\s*>\s*)\s*([A-Za-z0-9]{3})/;

export const isFlightItineraryItem = (item) => {
  if (!item) return false;
  const category = String(item.category || '').toLowerCase();
  if (category === 'flight') return true;

  const type = String(item.transportation_type || '').toLowerCase();
  if (category === 'transport' && type === 'flight') return true;

  // Package / legacy rows: category mapped to transport but type left blank.
  // Treat as a flight when the payload looks like one, or common flight labels.
  if (category === 'transport' && !type) {
    const data = parseTransportationData(item.transportation_data);
    if (
      data?.flight_number
      || data?.airline
      || data?.airline_code
      || data?.departure_airport
      || data?.arrival_airport
      || data?.origin
      || data?.destination
      || (Array.isArray(data?.legs) && data.legs.length > 0)
    ) {
      return true;
    }
    const name = String(item.name || '').trim().toLowerCase();
    if (/^(outbound|return|flight)\b/.test(name) || /\bflight\b/.test(name)) {
      return true;
    }
  }

  return false;
};

export const normalizeAirportCode = (value) => {
  if (!value) {
    return null;
  }

  const trimmed = String(value).trim();
  if (!trimmed || trimmed.toUpperCase() === 'N/A') {
    return null;
  }

  const upper = trimmed.toUpperCase();
  if (/^[A-Z0-9]{3}$/.test(upper)) {
    return upper;
  }

  const parenMatch = trimmed.match(/\(([A-Za-z0-9]{3})\)/);
  if (parenMatch) {
    return parenMatch[1].toUpperCase();
  }

  const codeMatch = trimmed.match(/\b([A-Za-z0-9]{3})\b/);
  if (codeMatch) {
    return codeMatch[1].toUpperCase();
  }

  return null;
};

export const preferLocationInferredCode = (storedCode, inferredCode, locationText = '') => {
  if (!inferredCode) {
    return storedCode || null;
  }
  if (!storedCode) {
    return inferredCode;
  }
  if (storedCode === inferredCode) {
    return storedCode;
  }

  const lower = String(locationText).toLowerCase();
  if (lower.includes('san jose') && inferredCode === 'SJC' && storedCode === 'SAN') {
    return 'SJC';
  }
  if (lower.includes('buffalo') && inferredCode === 'BUF') {
    return 'BUF';
  }
  if (lower.includes('houston') && inferredCode === 'IAH') {
    return 'IAH';
  }

  return storedCode;
};

export const inferAirportFromLocation = (locationText) => {
  if (!locationText) {
    return null;
  }

  const trimmed = locationText.trim();
  const lower = trimmed.toLowerCase();

  for (const airport of AIRPORT_DATABASE) {
    const city = airport.city.toLowerCase();
    if (city.length > 2 && lower.includes(city)) {
      if (city === 'new york' && lower.includes('buffalo')) {
        continue;
      }
      if (city === 'san diego' && lower.includes('san jose')) {
        continue;
      }
      return airport.iata;
    }
  }

  if (/^[A-Za-z0-9]{3}$/.test(trimmed) || /\([A-Za-z0-9]{3}\)/.test(trimmed)) {
    return normalizeAirportCode(trimmed);
  }

  return null;
};

export const inferDestinationFromItem = (item) => {
  const texts = [item?.name, item?.description].filter(Boolean);

  for (const text of texts) {
    const route = parseRouteFromText(text);
    if (route?.to) {
      return route.to;
    }
  }

  return null;
};

export const parseTransportationData = (data) => {
  if (!data) return {};
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  return typeof data === 'object' ? data : {};
};

const readTransportField = (data, ...keys) => {
  for (const key of keys) {
    if (data?.[key]) {
      return data[key];
    }
  }
  return null;
};

const GENERIC_FLIGHT_NAME = /^(flight|transport|transportation)$/i;

export const getAirportRouteCity = (code) => {
  if (!code) return null;
  const normalized = normalizeAirportCode(code) || String(code).trim().toUpperCase();
  const airport = AIRPORT_DATABASE.find((entry) => entry.iata === normalized);
  if (!airport) return normalized;
  const nick = airport.label.match(/\s-\s([^(]+)\s*\(/);
  if (nick?.[1] && nick[1].trim().toUpperCase() !== airport.iata) {
    return nick[1].trim();
  }
  return airport.city;
};

export const formatCityToCityLabel = (fromCode, toCode) => {
  const fromCity = getAirportRouteCity(fromCode);
  const toCity = getAirportRouteCity(toCode);
  if (fromCity && toCity && fromCode !== toCode) {
    return `${fromCity} to ${toCity}`;
  }
  return fromCity || toCity || null;
};

const parseRouteFromText = (text = '') => {
  if (!text) {
    return null;
  }

  const arrowMatch = text.match(ARROW_PATTERN);
  if (!arrowMatch) {
    return null;
  }

  return {
    from: arrowMatch[1].toUpperCase(),
    to: arrowMatch[2].toUpperCase(),
  };
};

const readLegAirport = (leg, direction) => {
  if (direction === 'from') {
    return normalizeAirportCode(
      readTransportField(
        leg,
        'departure_airport',
        'departureAirport',
        'origin',
        'origin_airport',
        'from_airport',
        'from'
      )
    );
  }

  return normalizeAirportCode(
    readTransportField(
      leg,
      'arrival_airport',
      'arrivalAirport',
      'destination',
      'destination_airport',
      'to_airport',
      'to'
    )
  );
};

export const getFlightLegRouteLabel = (leg, legIndex = 0, allLegs = []) => {
  if (!leg) {
    return null;
  }

  let from = readLegAirport(leg, 'from');
  let to = readLegAirport(leg, 'to');

  if (legIndex > 0 && allLegs.length > 1) {
    const previousArrival = readLegAirport(allLegs[legIndex - 1], 'to');
    const journeyOrigin = readLegAirport(allLegs[0], 'from');
    if (previousArrival && from === journeyOrigin && from !== previousArrival) {
      from = previousArrival;
    } else if (!from && previousArrival) {
      from = previousArrival;
    }
  }

  if (!to && legIndex < allLegs.length - 1) {
    to = readLegAirport(allLegs[legIndex + 1], 'from');
  }

  if (from && to) {
    return `${from} → ${to}`;
  }
  if (from) {
    return from;
  }
  if (to) {
    return to;
  }

  return null;
};

const getLegRouteParts = (leg = {}) => {
  const from = readLegAirport(leg, 'from');
  const to = readLegAirport(leg, 'to');

  if (from && to) {
    return { from, to };
  }

  return null;
};

const resolveFlightEndpoints = (item, data, storedLegs) => {
  const inferredOrigin = inferAirportFromLocation(item?.location);

  if (storedLegs.length > 0) {
    const firstLeg = storedLegs[0];
    const lastLeg = storedLegs[storedLegs.length - 1];
    let from = preferLocationInferredCode(
      readLegAirport(firstLeg, 'from'),
      inferredOrigin,
      item?.location
    );
    let to = readLegAirport(lastLeg, 'to');

    if (!to) {
      to = normalizeAirportCode(
        readTransportField(
          data,
          'arrival_airport',
          'arrivalAirport',
          'destination_airport',
          'destination',
          'to_airport',
          'to'
        )
      );
    }

    if (!to && storedLegs.length >= 2) {
      const inferredDestination = inferDestinationFromItem(item);
      if (inferredDestination && inferredDestination !== readLegAirport(lastLeg, 'from')) {
        to = inferredDestination;
      } else {
        to = readLegAirport(lastLeg, 'from');
      }
    }

    if (!to && storedLegs.length === 1) {
      to = inferAirportFromLocation(item?.description);
    }

    if (from && to) {
      return { from, to };
    }
  }

  let from = preferLocationInferredCode(
    normalizeAirportCode(
      readTransportField(
        data,
        'departure_airport',
        'departureAirport',
        'origin_airport',
        'origin',
        'from_airport',
        'from'
      )
    ),
    inferredOrigin,
    item?.location
  );
  let to = normalizeAirportCode(
    readTransportField(
      data,
      'arrival_airport',
      'arrivalAirport',
      'destination_airport',
      'destination',
      'to_airport',
      'to'
    )
  );

  const nameRoute = parseRouteFromText(item?.name);
  if (nameRoute) {
    from = from || nameRoute.from;
    to = to || nameRoute.to;
  }

  const descriptionRoute = parseRouteFromText(item?.description);
  if (descriptionRoute) {
    from = from || descriptionRoute.from;
    to = to || descriptionRoute.to;
  }

  const routeLine = (item?.description || '')
    .split('\n')
    .find((line) => line.trim().toLowerCase().startsWith('route:'));
  if (routeLine) {
    const routeMatch = routeLine.match(/Route:\s*(.+?)\s*(?:→|->|—)\s*(.+)/i);
    if (routeMatch) {
      from = from || normalizeAirportCode(routeMatch[1]) || routeMatch[1].trim();
      to = to || normalizeAirportCode(routeMatch[2]) || routeMatch[2].trim();
    }
  }

  if (from && to) {
    return { from, to };
  }

  return null;
};

export const getFlightRouteParts = (item) => {
  if (!isFlightItineraryItem(item)) {
    return null;
  }

  const data = parseTransportationData(item?.transportation_data);
  const storedLegs = Array.isArray(data.legs) ? data.legs : [];

  if (storedLegs.length > 0) {
    const firstLegRoute = getLegRouteParts(storedLegs[0]);
    const lastLegRoute = getLegRouteParts(storedLegs[storedLegs.length - 1]);
    if (firstLegRoute?.from && lastLegRoute?.to) {
      return { from: firstLegRoute.from, to: lastLegRoute.to };
    }
  }

  return resolveFlightEndpoints(item, data, storedLegs);
};

export const getFlightRouteLabel = (item) => {
  const route = getFlightRouteParts(item);
  if (!route) {
    return null;
  }

  return `${route.from} → ${route.to}`;
};

export const buildConsolidatedFlightLocationLabel = (flightItems = []) => {
  if (!flightItems.length) {
    return null;
  }

  if (flightItems.length === 1) {
    const routeLabel = getFlightRouteLabel(flightItems[0]);
    if (routeLabel) {
      return routeLabel;
    }
  }

  const sortedItems = [...flightItems].sort((a, b) => {
    const timeA = a.start_datetime ? new Date(a.start_datetime).getTime() : 0;
    const timeB = b.start_datetime ? new Date(b.start_datetime).getTime() : 0;
    return timeA - timeB;
  });

  const firstItem = sortedItems[0];
  const lastItem = sortedItems[sortedItems.length - 1];
  const from = preferLocationInferredCode(
    readLegAirport(getFirstLeg(firstItem), 'from') ||
      normalizeAirportCode(firstItem.transportation_data?.departure_airport),
    inferAirportFromLocation(firstItem.location),
    firstItem.location
  );
  const to = readLegAirport(getLastLeg(lastItem), 'to') ||
    normalizeAirportCode(lastItem.transportation_data?.arrival_airport) ||
    inferAirportFromLocation(lastItem.location);

  if (from && to) {
    return `${from} → ${to}`;
  }

  return null;
};

const getFirstLeg = (item) => {
  const legs = item?.transportation_data?.legs;
  return Array.isArray(legs) && legs.length ? legs[0] : item?.transportation_data || {};
};

const getLastLeg = (item) => {
  const legs = item?.transportation_data?.legs;
  return Array.isArray(legs) && legs.length ? legs[legs.length - 1] : item?.transportation_data || {};
};


export const getFlightRouteSummary = (items = []) =>
  items
    .map((item) => getFlightRouteLabel(item))
    .filter(Boolean)
    .join(', ');

const formatLegFlightNumber = (leg = {}) => {
  const airline = readTransportField(leg, 'airline') || '';
  const flightNumber = readTransportField(leg, 'flight_number', 'flightNumber') || '';
  return `${airline} ${flightNumber}`.trim() || null;
};

export const getFlightNumberLabel = (item) => {
  const data = parseTransportationData(item?.transportation_data);
  const combined = formatLegFlightNumber(data);
  if (combined) {
    return combined;
  }

  const fromLegs = (Array.isArray(data.legs) ? data.legs : [])
    .map(formatLegFlightNumber)
    .filter(Boolean);
  if (fromLegs.length) {
    return [...new Set(fromLegs)].join(' · ');
  }

  const name = item?.name || '';
  const flightNumberMatch = name.match(/\b([A-Za-z]{1,3})\s*(\d{1,4})\b/);
  if (flightNumberMatch) {
    return `${flightNumberMatch[1].toUpperCase()} ${flightNumberMatch[2]}`;
  }

  const withoutRoute = name.replace(ARROW_PATTERN, '').replace(/^:\s*/, '').trim();
  if (withoutRoute && !GENERIC_FLIGHT_NAME.test(withoutRoute)) {
    return withoutRoute;
  }
  return null;
};

export const getFlightCityRouteLabel = (item) => {
  const route = getFlightRouteParts(item);
  if (route?.from || route?.to) {
    return formatCityToCityLabel(route.from, route.to);
  }
  const inferred = inferAirportFromLocation(item?.location);
  return inferred ? getAirportRouteCity(inferred) : null;
};

export const getFlightCardTitle = (item) => {
  const cityRoute = getFlightCityRouteLabel(item);
  if (cityRoute) {
    return cityRoute;
  }

  const routeLabel = getFlightRouteLabel(item);
  if (routeLabel) {
    return routeLabel;
  }

  const flightNumberLabel = getFlightNumberLabel(item);
  if (flightNumberLabel) {
    return flightNumberLabel;
  }

  if (item?.name && !GENERIC_FLIGHT_NAME.test(item.name)) {
    return item.name;
  }

  return item?.description || item?.location || 'Flight';
};
